/**
 * One artifact, inline in a collaboration turn (task 828).
 *
 * When a turn produces a file, the transcript carries a REFERENCE to it (task
 * 826, report §P7) — id, version, name, kind, content type, size, and no bytes.
 * Rendered as JSON that is an unreadable blob of ids; rendered as this, it is
 * the thing the turn made, with the two things a reader wants to do to it.
 *
 * **It costs no request.** Everything on the card is in the reference the turn
 * already delivered, which is why a transcript of twenty turns does not become
 * twenty fetches. The only call it can make is the download, and only when
 * asked.
 *
 * **Revise is the point of the reference.** Handing an artifact back as the base
 * for the next turn is what makes the engine append a version instead of opening
 * a new artifact (task 826) — the same `baseArtifact` vocabulary the UI tier
 * already uses. The card raises it; the surface that owns the composer decides
 * what to do with it, so this stays a card and not a controller.
 */
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useOptionalToast } from '@/contexts/ToastContext';
import { useDownloadArtifactVersion } from '@/hooks/useArtifacts';
import { ArtifactKindBadge } from './ArtifactKindBadge';
import { formatArtifactSize } from '@/types/artifacts';
import type { ArtifactReference } from '@/types/artifacts';

interface ArtifactCardProps {
  artifact: ArtifactReference;
  /**
   * Offer "Revise", handing this reference back as the base for the next turn.
   * Omitted where there is no composer to hand it to — a stored transcript on a
   * page that cannot send.
   */
  onRevise?: (artifact: ArtifactReference) => void;
  className?: string;
}

export function ArtifactCard({ artifact, onRevise, className }: ArtifactCardProps) {
  const download = useDownloadArtifactVersion();
  const toast = useOptionalToast();

  const handleDownload = () => {
    download.mutate(
      {
        artifactId: artifact.id,
        versionNumber: artifact.versionNumber,
        filename: artifact.name,
      },
      {
        onError: () =>
          toast?.error(
            'Could not download',
            'The file may have been deleted since this turn produced it.'
          ),
      }
    );
  };

  return (
    <div
      data-testid="artifact-card"
      data-artifact-id={artifact.id}
      data-version-number={artifact.versionNumber}
      className={`flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800 ${className ?? ''}`}
    >
      <ArtifactKindBadge kind={artifact.kind} iconOnly />

      <div className="min-w-0 flex-1">
        <p
          data-testid="artifact-card-name"
          className="truncate text-sm font-medium text-gray-900 dark:text-white"
        >
          {artifact.name}
        </p>
        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
          <span data-testid="artifact-card-size">{formatArtifactSize(artifact.sizeBytes)}</span>
          {' · '}
          <span className="font-mono">{artifact.contentType}</span>
          {' · '}v{artifact.versionNumber}
        </p>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={handleDownload}
          disabled={download.isPending}
          data-testid="artifact-card-download"
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <ArrowDownTrayIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {download.isPending ? 'Downloading…' : 'Download'}
        </button>

        {onRevise && (
          <button
            type="button"
            onClick={() => onRevise(artifact)}
            data-testid="artifact-card-revise"
            className="inline-flex items-center gap-1 rounded-md border border-transparent bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Revise
          </button>
        )}
      </div>
    </div>
  );
}

export default ArtifactCard;
